import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { View } from 'react-native';
import Notification, {
  NotificationType,
  NotificationProps,
} from '@/components/ui/therapist/Notification';
import { styles } from '@/components/ui/therapist/Notification/style';
import { randomUUID } from 'expo-crypto';
import { isPatientApp } from '@/config/constants';
import Toast, { ToastPosition } from 'react-native-toast-message';

interface NotificationItem
  extends Omit<NotificationProps, 'onHide' | 'duration' | 'children'> {
  id: string;
  duration: number;
  children: React.ReactNode;
}

interface NotificationContextType {
  showNotification: (options: {
    message?: string;
    type: NotificationType;
    duration?: number;
    children?: React.ReactNode;
    position?: ToastPosition;
    title: string;
  }) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const showNotification = useCallback(
    ({
      message,
      title,
      type = 'info',
      duration = 3000,
      children,
      position,
    }: {
      message?: string;
      title: string;
      type?: NotificationType;
      duration?: number;
      children?: ReactNode;
      position?: ToastPosition;
    }) => {
      if (isPatientApp) {
        Toast.show({
          type: type === 'alert' ? 'error' : type,
          text1: message || title || '',
          visibilityTime: duration,
          position,
        });
        return;
      }
      const id = randomUUID();
      const newNotification: NotificationItem = {
        id,
        title,
        message,
        type,
        duration,
        children,
      };
      setNotifications((prev) => [...prev, newNotification]);
    },
    [],
  );

  const hideNotification = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id),
    );
  }, []);

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      <View style={styles.notificationContainer} pointerEvents="box-none">
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            title={notification.title}
            duration={notification.duration}
            onHide={() => hideNotification(notification.id)}
            children={notification.children}
          />
        ))}
      </View>
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotification must be used within a NotificationProvider',
    );
  }
  return context;
};
